import { z } from 'zod';
import { UpholsteryDesignKeySchema } from './upholstery-design.schema';

// ═══════════════════════════════════════════════════
//  Upholstery design catalog rows
// ═══════════════════════════════════════════════════
//
// One row of the "Design Type" dropdown, as served by
// GET /api/v1/upholstery-designs.
//
// Not to be confused with UpholsteryDesignSchema in upholstery-design.schema.ts:
// that one is the design *configuration* saved on a component (type, channel
// width, button hardware…). This one is the *catalog entry* the dropdown lists.
//
// `key` is validated by shape, not by membership in a compile-time list: the
// upholstery_designs table is the source of truth for what a component may
// store, so a fixed enum here could only ever be a stale copy of it.
//
// This schema is the type source, not a runtime gate: nothing calls .parse()
// on it, and the client hook casts the response via
// apiClient.get<UpholsteryDesignOption[]>. The runtime guard against
// unexpected rows — an unrecognized key, a nullish selected type — lives
// client-side in toDesignTypeOptions (packages/client/src/utils/upholstery-design-options.ts).

export const UpholsteryDesignOptionSchema = z.object({
  id: z.string().uuid(),
  key: UpholsteryDesignKeySchema,
  name: z.string().min(1),
  sort_order: z.number().int(),
  active: z.boolean(),
  has_channels: z.boolean(),
  channel_axis: z.enum(['run', 'cross']).nullable(),
  is_tufted: z.boolean(),
  has_buttons: z.boolean(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type UpholsteryDesignOption = z.infer<typeof UpholsteryDesignOptionSchema>;

// ═══════════════════════════════════════════════════
//  Write payloads
// ═══════════════════════════════════════════════════

/** Trait fields, shared by create and update. */
const DesignTraitFields = {
  has_channels: z.boolean().default(false),
  channel_axis: z.enum(['run', 'cross']).nullable().default(null),
  is_tufted: z.boolean().default(false),
  has_buttons: z.boolean().default(false),
};

/**
 * `key` is optional on create: the server derives it from `name` when it is
 * omitted, which is what the UI does. Supplying it explicitly is allowed so a
 * caller can pin an exact key.
 *
 * `sort_order` is not accepted on create at all — a new design always lands at
 * max + 1, because the eight existing positions are hand-picked and inserting
 * into the middle would move them.
 */
export const CreateUpholsteryDesignSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120),
  key: UpholsteryDesignKeySchema.optional(),
  active: z.boolean().default(true),
  ...DesignTraitFields,
});

/**
 * No `key`. It is the value stored inside every saved configuration and the
 * value the cost helpers branch on, so changing it would orphan them. The
 * service rejects a `key` that arrives anyway rather than dropping it silently
 * — a UI bug that tries to rename a key should be visible, not absorbed.
 */
export const UpdateUpholsteryDesignSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  sort_order: z.number().int().min(0).optional(),
  active: z.boolean().optional(),
  has_channels: z.boolean().optional(),
  channel_axis: z.enum(['run', 'cross']).nullable().optional(),
  is_tufted: z.boolean().optional(),
  has_buttons: z.boolean().optional(),
}).strict();

export type CreateUpholsteryDesign = z.infer<typeof CreateUpholsteryDesignSchema>;
export type UpdateUpholsteryDesign = z.infer<typeof UpdateUpholsteryDesignSchema>;
